import type { HTMLAttributes } from "react";
import { cn } from "@/shared/lib/cn";
import { Surface } from "./Surface";

/** A table whose feature-defined grid columns can collapse into readable cards. */
export function ResponsiveTable({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <Surface role="table" tone="paper" radius="lg"
    className={cn("studio-table-frame min-w-0 overflow-hidden", className)} {...props} />;
}

/** Keep this row visible at the same breakpoint as the feature's column layout. */
export function ResponsiveTableHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div role="row"
    className={cn("min-h-10 border-y border-line-soft bg-[var(--table-heading)] px-3 py-2 text-[12px] font-semibold text-ink-soft", className)}
    {...props} />;
}

export interface ResponsiveTableRowProps extends HTMLAttributes<HTMLDivElement> {
  selected?: boolean;
  archived?: boolean;
}

export function ResponsiveTableRow({ selected, archived, className, ...props }: ResponsiveTableRowProps) {
  return <div role="row" aria-selected={selected} data-selected={selected || undefined}
    className={cn(
      "min-w-0 border-b border-line-soft bg-paper px-3 py-4 last:border-b-0 even:bg-[var(--table-stripe)] hover:bg-[var(--table-hover)] data-[selected]:bg-[var(--selection-bg)]",
      archived && "text-ink-faint",
      className,
    )} {...props} />;
}

export function ResponsiveTableCell({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div role="cell" className={cn("min-w-0 text-[13px] text-ink", className)} {...props} />;
}

export function ResponsiveTableHead({ className, ...props }: HTMLAttributes<HTMLSpanElement>) {
  return <span role="columnheader" className={cn("min-w-0", className)} {...props} />;
}
